// SPDX-License-Identifier: AGPL-3.0-only
import { useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, Group, Loader, Stack, Table, Text, TextInput, Title, VisuallyHidden } from '@mantine/core';

import { ApiError, api } from '../api/client';
import type { Company, Site } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { GatewayEnrolment } from './GatewayEnrolment';

/**
 * Organization → companies → sites.
 *
 * # THIS PAGE IS THE PHASE 1 PROOF, NOT DECORATION
 *
 * It demonstrates the thing that actually matters: THE TENANT CONTEXT DECIDES
 * WHAT IS SHOWN. Nothing here sends an organization id — the session does. Two
 * users of different organizations hitting the same URL see different data, and
 * neither can ask for the other's, because the tenant is derived from the
 * authenticated identity server-side and never from the request.
 *
 * Selecting a company narrows the site list through `?company_id=`, which is a
 * NARROWING filter: a company id from another tenant matches nothing, because
 * the repository predicate and row-level security both scope to the session's
 * organization first.
 */
/**
 * A one-field inline creator.
 *
 * Deliberately plain: Phase 1 needs to prove the write loop reaches the API,
 * authorizes, audits, and refreshes — not to explore form design.
 *
 * It renders the SERVER'S field errors. A 422 names the field and the
 * constraint and never echoes the submitted value, so showing it verbatim is
 * both safe and more accurate than a guess made here.
 */
function InlineCreate({
  label,
  disabled,
  onCreate,
}: {
  label: string;
  disabled?: boolean;
  onCreate: (name: string) => Promise<void>;
}) {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await onCreate(name.trim());
      setName('');
    } catch (err) {
      setError(err instanceof ApiError ? err : null);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit}>
      <Stack gap="xs" mt="sm">
        <Group gap="xs" align="flex-end">
          <TextInput
            style={{ flex: 1 }}
            size="xs"
            placeholder={label}
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
            error={error?.fieldErrors['name']}
            disabled={disabled}
            required
          />
          <Button type="submit" size="xs" loading={busy} disabled={disabled}>
            Add
          </Button>
        </Group>
        {error && !error.fieldErrors['name'] && (
          <Text size="xs" c="red">
            {error.problem.detail}
            {error.requestId && <> (request {error.requestId})</>}
          </Text>
        )}
      </Stack>
    </form>
  );
}

export function Tenancy() {
  const { organization } = useAuth();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  // The site whose Gateway enrolment is open. Held as an id and resolved
  // against the CURRENT list below, so a site that disappears from the list
  // cannot leave a stale panel open against it.
  const [gatewaySiteID, setGatewaySiteID] = useState<string | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [loading, setLoading] = useState(true);
  // Bumped after a successful write so the effect refetches. The UI shows what
  // the SERVER returned, never an optimistic guess: an entity the server
  // rejected must not appear to exist.
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([api.companies(), api.sites(selected ?? undefined)])
      .then(([c, s]) => {
        if (cancelled) return;
        setCompanies(c.items);
        setSites(s.items);
        setError(null);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof ApiError ? err : null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selected, reloadToken]);

  // Resolved from the CURRENT list, so a filter change that hides the site also
  // closes its panel instead of leaving one open against something not shown.
  const openGatewaySite = sites.find((s) => s.id === gatewaySiteID) ?? null;

  if (error) {
    return (
      <Alert color="red" title={error.problem.title}>
        {error.problem.detail}
        {error.requestId && (
          <Text size="xs" c="dimmed" mt="xs">
            Request ID: <code>{error.requestId}</code>
          </Text>
        )}
      </Alert>
    );
  }

  return (
    <Stack>
      <Card withBorder>
        <Group justify="space-between">
          <div>
            <Text size="xs" c="dimmed">Organization</Text>
            <Title order={3}>{organization?.name}</Title>
          </div>
          {/* The tenant identity, shown deliberately: this is what scopes every
              request, and making it visible makes "am I looking at the right
              customer?" answerable at a glance. */}
          <Badge variant="light" size="lg">{organization?.id.slice(0, 8)}</Badge>
        </Group>
      </Card>

      {loading ? (
        <Group justify="center" p="xl"><Loader /></Group>
      ) : (
        <Group align="flex-start" grow>
          <Card withBorder>
            <Title order={4} mb="sm">Companies</Title>
            {companies.length === 0 ? (
              <Text c="dimmed" size="sm">No companies yet.</Text>
            ) : (
              <Table highlightOnHover>
                <Table.Tbody>
                  {companies.map((c) => (
                    <Table.Tr
                      key={c.id}
                      onClick={() => setSelected(selected === c.id ? null : c.id)}
                      style={{ cursor: 'pointer' }}
                      bg={selected === c.id ? 'var(--mantine-color-blue-light)' : undefined}
                    >
                      <Table.Td>{c.name}</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
            <InlineCreate
              label="New company name"
              onCreate={async (name) => {
                await api.createCompany(name);
                setReloadToken((n) => n + 1);
              }}
            />
          </Card>

          <Card withBorder>
            <Title order={4} mb="sm">
              Sites {selected && <Text span c="dimmed" size="sm">(filtered)</Text>}
            </Title>
            {sites.length === 0 ? (
              <Text c="dimmed" size="sm">No sites.</Text>
            ) : (
              <Table>
                <Table.Tbody>
                  {sites.map((s) => (
                    <Table.Tr key={s.id}>
                      <Table.Td>{s.name}</Table.Td>
                      <Table.Td align="right">
                        {/* A real button, not a clickable row: a row with an
                            onClick cannot be reached by keyboard and announces
                            nothing to a screen reader. The accessible name
                            includes the site, so a list of them is not four
                            identical "Gateways" buttons. */}
                        <Button
                          size="xs"
                          variant={gatewaySiteID === s.id ? 'filled' : 'light'}
                          onClick={() => setGatewaySiteID(gatewaySiteID === s.id ? null : s.id)}
                        >
                          Gateways
                          <VisuallyHidden> for {s.name}</VisuallyHidden>
                        </Button>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
            <InlineCreate
              label={selected ? 'New site name' : 'Select a company first'}
              disabled={!selected}
              onCreate={async (name) => {
                if (!selected) return;
                await api.createSite(selected, name);
                setReloadToken((n) => n + 1);
              }}
            />
          </Card>
        </Group>
      )}

      {/* Site → Gateway enrolment. Rendered under the tenancy columns rather
          than inside the sites card so it has the full width on a phone. */}
      {openGatewaySite && <GatewayEnrolment key={openGatewaySite.id} site={openGatewaySite} />}
    </Stack>
  );
}
